'use client';

/**
 * A collection of React Hook
 *
 * @packageDocumentation
 * @category Hook
 */

//#region imports
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef } from 'react';

//#endregion

declare global {
  interface Window {
    google: any;
    fbAsyncInit: any;
    FB: any;
  }
}

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 useCurrentPage
 *--------------------------------------------------------------------------------------------------------------------------
 *
 * Get the name of the current page from the URL.
 * This value is automatically updated as the user navigates between pages
 *
 * @returns {String} Name of page extracted from URL path
 */
export const useCurrentPage = () => {
  const pathname = usePathname();
  const currentPage = useMemo(() => {
    const segments = pathname?.split('/').filter(Boolean) || [];
    if (segments.length <= 1) return '/';
    return segments[segments.length - 1];
  }, [pathname]);

  return currentPage;
};

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 useCurrentPage
 *--------------------------------------------------------------------------------------------------------------------------
 *
 * Verify if current URL path is a details page
 *
 * @returns {Boolean} True if the current path is a details page, false otherwise
 */
export const useIsDetailsPage = () => {
  const pathname = usePathname();
  const isDetailsPage = useMemo(() => Boolean(Number(pathname?.split('/').pop())), [pathname]);

  return isDetailsPage;
};

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 useTimeout
 *--------------------------------------------------------------------------------------------------------------------------
 *
 * Applies a `delay` (timeout) before calling `callback`.
 *
 * @returns {Object} An object container clear and reset functions
 */
export const useTimeout = (callback: () => void, delay: number) => {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const set = useCallback(() => {
    timeoutRef.current = setTimeout(() => callbackRef.current(), delay);
  }, [delay]);

  const clear = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <necessary to set and clear timeout>
  useEffect(() => {
    set();
    return clear;
  }, [delay, set, clear]);

  const reset = useCallback(() => {
    clear();
    set();
  }, [clear, set]);

  return { clear, reset };
};

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 useDebounce
 *--------------------------------------------------------------------------------------------------------------------------
 *
 * Applies a `delay` (timeout) before calling `callback`.
 * Useful when querying APIs based on user input (like in a search field), to reduce stress on API server
 */
export const useDebounce = (callback: () => void, delay: number, dependencies: any[]) => {
  const { reset, clear } = useTimeout(callback, delay);
  useEffect(reset, [...dependencies, reset]);
  useEffect(clear, [clear]);
};

export const useFacebook = (appId?: string, version = 'v7.0') => {
  if (appId && typeof window != 'undefined') {
    // Initialize Facebook
    window.fbAsyncInit = function () {
      window.FB.init({
        appId,
        cookie: true,
        version,
        xfbml: true,
      });

      window.FB.AppEvents.logPageView();
    };

    (function (d, s, id) {
      const fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) {
        return;
      }
      const js: any = d.createElement(s);
      js.id = id;
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      fjs.parentNode?.insertBefore(js, fjs);
    })(document, 'script', 'facebook-jssdk');
  }
};
